import { useFocusTrap } from "../hooks/useFocusTrap";
import { safeUrl } from "../lib/safeUrl";
/**
 * CCTV Stream Modal
 *
 * Opens when the operator clicks a camera dot on the map.
 * Shows:
 *   1. Live snapshot (imageUrl, auto-refreshed every 10 s)
 *   2. HLS video player (hlsUrl, via native <video> — browsers handle HLS on macOS/iOS
 *      natively; on Chrome you'd add hls.js, but that's a future enhancement)
 *   3. Latest CV detections from GET /api/cctv/cv-events?cameraId=
 *   4. Camera metadata
 *
 * HLS fallback strategy:
 *   hlsUrl present  → try <video src={hlsUrl} autoPlay muted playsInline>
 *   imageUrl only   → polling img refresh every 10 s (MJPEG-style)
 *   neither         → "No stream available" message
 *
 * The component owns its own fetch loop for CV events so it doesn't
 * pollute the global feed state.
 */

import { useState, useEffect, useRef } from "react";
import type { CvDetectionEvent } from "@chonburi/shared";
import { CCTV_CV_POLL_MS, CCTV_SNAPSHOT_POLL_MS } from "../lib/feedPoll";
import { nextPollDelayMs, pageHidden, startGuardedPoll } from "../lib/pollGuard";

interface CctvCamera {
  id: string;
  name: string;
  lat: number;
  lng: number;
  vendor?: string;
  imageUrl?: string;
  hlsUrl?: string;
  organization?: string;
  snapshotPath?: string;
  sourceUrl?: string;
  note?: string;
}

interface Props {
  camera: CctvCamera | null;
  onClose: () => void;
  /** Base URL for the API — defaults to empty string (same origin). */
  apiBase?: string;
}

const CV_POLL_MS = CCTV_CV_POLL_MS;
const IMG_REFRESH_MS = CCTV_SNAPSHOT_POLL_MS;

function DetectionBadge({ cls, count }: { cls: string; count: number }) {
  const label = cls.charAt(0).toUpperCase() + cls.slice(1);
  return (
    <span className="cctv-det-badge mono">
      {label} {count > 1 && <strong>{count}</strong>}
    </span>
  );
}

export function CctvStreamModal({ camera, onClose, apiBase = "" }: Props) {
  const containerRef = useFocusTrap(Boolean(camera));
  const [cvEvent, setCvEvent] = useState<CvDetectionEvent | null>(null);
  const [imgTs, setImgTs] = useState(Date.now());
  const [videoError, setVideoError] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotTime, setSnapshotTime] = useState<string | null>(null);
  const [snapshotStale, setSnapshotStale] = useState(true);
  const [imageError, setImageError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgFails = useRef(0);
  const rearmImg = useRef<() => void>(() => {});

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // CV detection poll — pauses in a hidden tab and backs off after errors.
  const cameraId = camera?.id;
  useEffect(() => {
    if (!cameraId) return;
    let active = true;

    const poll = startGuardedPoll(async () => {
      if (!active) return "skip";
      try {
        const res = await fetch(
          `${apiBase}/api/cctv/cv-events?cameraId=${encodeURIComponent(cameraId)}&limit=1`,
        );
        if (!active) return "skip";
        if (!res.ok) return "fail";
        const data = await res.json() as { events: CvDetectionEvent[] };
        const event = data.events[0];
        const age = event ? Date.now() - Date.parse(event.timestamp) : NaN;
        setCvEvent(Number.isFinite(age) && age >= -300_000 && age <= 300_000 ? event : null);
        return "ok";
      } catch {
        // CV pipeline may not be running yet — still back off so a dead
        // endpoint does not stay on the 10s floor.
        return active ? "fail" : "skip";
      }
    }, CV_POLL_MS);

    return () => { active = false; poll.stop(); };
  }, [apiBase, cameraId]);

  // Fetch only the known public snapshot route. Capture time is independent of fetch time.
  useEffect(() => {
    if (!camera?.snapshotPath || !/^\/api\/cctv\/dwr\/[a-f0-9-]+\/snapshot$/.test(camera.snapshotPath)) return;
    let active = true;
    let objectUrl: string | null = null;
    const ctrl = new AbortController();
    const load = async () => {
      try {
        const r = await fetch(`${apiBase}${camera.snapshotPath}`, { signal: ctrl.signal });
        if (!r.ok) throw new Error("Snapshot unavailable");
        const blob = await r.blob();
        if (!active) return;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = URL.createObjectURL(blob);
        setSnapshotUrl(objectUrl);
        setSnapshotTime(r.headers.get("x-observed-at"));
        setSnapshotStale(r.headers.get("x-snapshot-stale") !== "false");
        setImageError(false);
      } catch { if (active) { setSnapshotStale(true); setImageError(true); } }
    };
    load();
    const timer = setInterval(load, 60_000);
    return () => { active = false; ctrl.abort(); clearInterval(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [camera?.snapshotPath, apiBase]);

  // Snapshot refresh (imageUrl only, no HLS). 10s floor, pause when hidden,
  // and stretch the interval after consecutive image errors.
  useEffect(() => {
    if (!camera?.imageUrl || camera.hlsUrl) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const arm = () => {
      if (timer !== undefined) clearTimeout(timer);
      if (stopped) return;
      const delay = nextPollDelayMs(IMG_REFRESH_MS, imgFails.current, pageHidden());
      if (delay === null) return;
      timer = setTimeout(() => {
        setImgTs(Date.now());
        arm();
      }, delay);
    };
    rearmImg.current = arm;

    const onVis = () => {
      if (pageHidden()) {
        if (timer !== undefined) clearTimeout(timer);
        return;
      }
      setImgTs(Date.now());
      arm();
    };
    document.addEventListener("visibilitychange", onVis);
    arm();
    return () => {
      stopped = true;
      rearmImg.current = () => {};
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [camera?.hlsUrl, camera?.id, camera?.imageUrl]);

  // Reset video error when camera changes
  useEffect(() => {
    setVideoError(false);
    setCvEvent(null);
    imgFails.current = 0;
    setSnapshotUrl(null);
    setSnapshotTime(null);
    setSnapshotStale(true);
    setImageError(false);
  }, [camera?.id]);

  if (!camera) return null;

  const hls = safeUrl(camera.hlsUrl);
  const image = snapshotUrl ?? safeUrl(camera.imageUrl);
  const hasHls = !!hls && !videoError;
  const hasImg = !!image && !imageError;

  return (
    <div ref={containerRef} className="cctv-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <aside className="cctv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className="cctv-modal-head">
          <div>
            <span className="eyebrow mono">CCTV · {camera.vendor ?? camera.organization ?? "CAMERA"}</span>
            <h3 className="cctv-modal-title">{camera.name}</h3>
            <div className="cctv-modal-coords mono">
              {camera.lat.toFixed(5)}, {camera.lng.toFixed(5)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="building-card-close mono">
            ESC
          </button>
        </header>

        <p className="caption">{camera.note ?? "Public camera · stream availability unverified."}</p>
        {snapshotTime && <p className="mono caption">{snapshotStale ? "STALE SNAPSHOT" : "CAPTURED"} · {new Date(snapshotTime).toLocaleString("en-GB", { timeZone: "Asia/Bangkok" })} ICT</p>}
        {safeUrl(camera.sourceUrl) && <a href={camera.sourceUrl} target="_blank" rel="noreferrer">Open official camera viewer ↗</a>}
        {/* Stream / snapshot */}
        <div className={`cctv-modal-stream${!hasHls && !hasImg ? " is-unavailable" : ""}`}>
          {hasHls ? (
            <video
              ref={videoRef}
              src={hls ?? undefined}
              autoPlay
              muted
              playsInline
              controls
              className="cctv-video"
              onError={() => setVideoError(true)}
            />
          ) : hasImg ? (
            <img
              src={snapshotUrl ?? `${image}${image?.includes("?") ? "&" : "?"}_ts=${imgTs}`}
              alt={`Camera snapshot — ${camera.name}`}
              className="cctv-snapshot"
              onLoad={(e) => {
                imgFails.current = 0;
                (e.target as HTMLImageElement).style.display = "";
                rearmImg.current();
              }}
              onError={(e) => {
                // DWR blob snapshots recover through their own 60 s loader.
                if (snapshotUrl) { setImageError(true); return; }
                imgFails.current += 1;
                (e.target as HTMLImageElement).style.display = "none";
                rearmImg.current();
              }}
            />
          ) : (
            <div className="cctv-no-stream mono">NO STREAM AVAILABLE</div>
          )}

          {/* CV detection overlay */}
          {cvEvent && Object.keys(cvEvent.counts).length > 0 && (
            <div className="cctv-det-overlay">
              {Object.entries(cvEvent.counts).map(([cls, count]) => (
                <DetectionBadge key={cls} cls={cls} count={count} />
              ))}
              <span className="cctv-det-ts mono">
                {new Date(cvEvent.timestamp).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>

        {/* CV summary */}
        {cvEvent && (
          <section className="cctv-modal-cv">
            <span className="eyebrow mono">LAST DETECTION</span>
            <div className="cctv-cv-row">
              <span className="mono" style={{ color: "var(--ink-mid)", fontSize: "0.7rem" }}>
                {cvEvent.model && `${cvEvent.model} · `}
                {new Date(cvEvent.timestamp).toLocaleString()}
              </span>
            </div>
            {cvEvent.detections.length > 0 && (
              <ul className="cctv-det-list">
                {cvEvent.detections.slice(0, 8).map((d, i) => (
                  <li key={i} className="cctv-det-item mono">
                    <span className="cctv-det-class">{d.class}</span>
                    <span className="cctv-det-conf" style={{ color: "var(--ink-mid)" }}>
                      {Math.round(d.confidence * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {cvEvent.snapshotUrl && (
              <a href={cvEvent.snapshotUrl} target="_blank" rel="noreferrer" className="mono cctv-snap-link">
                snapshot ↗
              </a>
            )}
          </section>
        )}

        {/* Metadata */}
        <footer className="cctv-modal-meta mono">
          <span>ID: {camera.id}</span>
          {camera.vendor && <span>VENDOR: {camera.vendor}</span>}
        </footer>
      </aside>
    </div>
  );
}
